import { css, html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { sanitizeMermaidSource } from "../lib/mermaid-sanitize.ts";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function renderMermaidSvg(graph: string, id: string, fillWidth = false): Promise<string> {
  const mermaid = (await import("mermaid")).default;
  mermaid.initialize({
    startOnLoad: false,
    theme: "base",
    securityLevel: "loose",
    themeVariables: {
      background: "#111827",
      primaryColor: "#fcd5b5",
      primaryTextColor: "#111827",
      primaryBorderColor: "#d97706",
      lineColor: "#a5b4fc",
      secondaryColor: "#0f766e",
      tertiaryColor: "#ddd6fe",
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    },
    flowchart: {
      useMaxWidth: fillWidth,
      htmlLabels: true,
      curve: "basis",
      diagramPadding: fillWidth ? 8 : 20,
      nodeSpacing: fillWidth ? 36 : 56,
      rankSpacing: fillWidth ? 32 : 48,
    },
  });
  const { svg } = await mermaid.render(id, graph);
  return svg;
}

@customElement("ead-mermaid-diagram")
export class EadMermaidDiagram extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    :host([embedded]) {
      display: block;
      width: 100%;
    }
    :host([embedded]) .ead-graph-section {
      margin-top: 0;
    }
    :host([embedded]) .ead-graph-heading {
      display: none;
    }
    :host([embedded]) .ead-graph-panel {
      border: none;
      border-radius: 0;
      background: transparent;
      padding: 0;
      min-height: 0;
      overflow: visible;
    }
    :host([embedded]) .ead-graph-panel svg {
      width: 100%;
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0;
    }
    :host([embedded]) .ead-graph-mount {
      width: 100%;
    }
    :host([embedded]) .ead-graph-source {
      margin-top: 0.5rem;
    }
    .ead-graph-section {
      margin-top: 0.5rem;
    }
    .ead-graph-heading {
      font-size: 1.05rem;
      font-weight: 650;
      color: #f1f5f9;
      margin: 0 0 0.85rem;
      padding-bottom: 0.35rem;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }
    .ead-graph-panel {
      overflow: auto;
      padding: 1rem 1.25rem;
      border: 1px solid rgba(167, 139, 250, 0.22);
      border-radius: 0.65rem;
      background: #0f172a;
      min-height: 180px;
    }
    .ead-graph-panel svg {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
    }
    .ead-graph-empty,
    .ead-graph-error {
      color: #94a3b8;
      font-size: 0.9rem;
      padding: 1rem 0;
    }
    .ead-graph-error {
      color: #fca5a5;
    }
    .ead-graph-source {
      margin-top: 0.75rem;
      color: #94a3b8;
      font-size: 0.82rem;
    }
    .ead-graph-source summary {
      cursor: pointer;
      user-select: none;
    }
    .ead-graph-source pre {
      margin: 0.5rem 0 0;
      padding: 0.75rem 1rem;
      border-radius: 8px;
      background: #0b1220;
      border: 1px solid rgba(148, 163, 184, 0.18);
      overflow-x: auto;
      font-family: "JetBrains Mono", "SF Mono", Monaco, monospace;
      font-size: 0.82rem;
      line-height: 1.5;
      color: #cbd5e1;
      white-space: pre-wrap;
    }
  `;

  @property({ type: String }) source = "";
  @property({ type: String }) heading = "EAD Graph";
  @property({ type: Boolean }) showHeading = true;
  @property({ type: Boolean }) showSourceToggle = true;
  @property({ type: Boolean }) compact = false;
  @property({ type: Boolean }) embedded = false;
  @property({ type: Number }) refreshKey = 0;

  private _renderRun = 0;

  firstUpdated(): void {
    if (this.embedded) this.setAttribute("embedded", "");
    void this._paint();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has("embedded")) {
      if (this.embedded) {
        this.setAttribute("embedded", "");
      } else {
        this.removeAttribute("embedded");
      }
    }
    if (changed.has("source") || changed.has("showSourceToggle") || changed.has("refreshKey")) {
      void this._paint();
    }
  }

  private async _paint(): Promise<void> {
    const mount = this.renderRoot.querySelector(".ead-graph-mount") as HTMLDivElement | null;
    if (!mount) return;

    const graph = sanitizeMermaidSource((this.source || "").trim());
    if (!graph) {
      mount.innerHTML = `<div class="ead-graph-empty">No diagram — add EAD Script tokens to generate.</div>`;
      return;
    }

    const runId = ++this._renderRun;
    mount.innerHTML = `<div class="ead-graph-empty">Rendering diagram…</div>`;

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    if (runId !== this._renderRun) return;

    try {
      const id = `ead-mmd-${runId}-${Date.now()}`;
      const svg = await renderMermaidSvg(graph, id, this.embedded);
      if (runId !== this._renderRun) return;
      const sourceBlock = this.showSourceToggle
        ? `<details class="ead-graph-source"><summary>Mermaid source</summary><pre>${escapeHtml(graph)}</pre></details>`
        : "";
      mount.innerHTML = `<div class="ead-graph-panel">${svg}</div>${sourceBlock}`;
    } catch (err) {
      if (runId !== this._renderRun) return;
      const message = err instanceof Error ? err.message : String(err);
      mount.innerHTML = `<div class="ead-graph-error">Could not render diagram: ${escapeHtml(message)}</div>`;
    }
  }

  render() {
    return html`
      <div class="ead-graph-section ${this.compact ? "compact" : ""}">
        ${this.showHeading ? html`<h2 class="ead-graph-heading">${this.heading}</h2>` : nothing}
        <div class="ead-graph-mount"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-mermaid-diagram": EadMermaidDiagram;
  }
}
